import {useEffect, useState, Fragment} from 'react';
import {useMutation} from 'services/apollo';
import {EzModal, EzLayout, EzHeading, EzLink} from '@ezcater/recipe';
import {useTranslation} from 'react-i18next';
import {formatPhoneNumber} from 'util/StringUtils';
import {trackPageView, trackButtonClick} from 'util/tracking/trackEvent';
import {COMPLIANCE_MODAL_PAGE_NAME, COMPLIANCE_MODAL_BUTTON} from 'util/tracking/trackingEvents';
import {DriverAcceptSmsTerms} from 'services/graphql';
import type {
  DriverAcceptSmsTermsMutation,
  DriverAcceptSmsTermsMutationVariables,
} from 'services/graphql/types';
import {TERMS_URL} from 'paths';
import {ErrorText} from 'styles/global.styles';

interface ComplianceModalProps {
  phone: string;
  driverId: string;
}

const ComplianceModal = ({phone, driverId}: ComplianceModalProps) => {
  const {t} = useTranslation();
  const [isModelOpen, setIsModalOpen] = useState(true);

  const [acceptSmsTerms, acceptSmsTermsResult] = useMutation<
    DriverAcceptSmsTermsMutation,
    DriverAcceptSmsTermsMutationVariables
  >(DriverAcceptSmsTerms, {variables: {input: {}}});

  const subheaderText = t('complianceModal.subheader', {
    phone: formatPhoneNumber(phone),
  });
  const termsText = t('complianceModal.terms');
  const buttonText = t('complianceModal.button');
  const defaultErrorText = t('complianceModal.error');

  useEffect(() => {
    trackPageView(COMPLIANCE_MODAL_PAGE_NAME, {
      driver_id: driverId,
      delivery_id: null,
    });
  }, [driverId]);

  const onSubmit = async () => {
    try {
      const mutationResult = await acceptSmsTerms();

      trackButtonClick(COMPLIANCE_MODAL_BUTTON, {
        driver_id: driverId,
        delivery_id: null,
      });

      if (!mutationResult.data?.driverAcceptSmsTerms?.userErrors?.length) {
        setIsModalOpen(false);
      }
    } catch (error) {
      // no op
    }
  };

  const resultErrors = acceptSmsTermsResult.data?.driverAcceptSmsTerms?.userErrors;

  return (
    <EzModal
      isOpen={isModelOpen}
      headerText=""
      submitLabel={buttonText}
      onSubmit={onSubmit}
      isSubmitting={acceptSmsTermsResult.loading}
    >
      <EzLayout layout={{base: 'stack', medium: 'equal'}} />
      <EzHeading size="4">
        <div style={{marginBottom: '1rem'}} />
        {subheaderText}&nbsp;&nbsp;
        <EzLink href={TERMS_URL} target="_blank">
          {termsText}
        </EzLink>
      </EzHeading>
      {acceptSmsTermsResult.error && <ErrorText>{defaultErrorText}</ErrorText>}
      {!!resultErrors?.length && (
        <ErrorText>
          {resultErrors.map((error, i) => (
            <Fragment key={i}>
              {error.message}
              <br />
            </Fragment>
          ))}
        </ErrorText>
      )}
    </EzModal>
  );
};

export default ComplianceModal;
